import {
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  useDisclosure,
  VStack,
} from "@chakra-ui/react"
import { HamburgerIcon } from "@chakra-ui/icons"
import { signOut, useSession } from "next-auth/react"
import NavigationButton from "./NavigationButton"
import WorkspaceSwitcher from "./WorkspaceSwitcher"
import { ExternalLinkIcon } from "@chakra-ui/icons"
import { Translation } from "next-i18next"
import useWorkspaceContext from "@/hooks/useWorkspaceContext"
import { marketingOrigin } from "@/utils/urls"

interface IMenuItems {
  title: JSX.Element
  link?: string
  icon?: any
  onClick?: () => void
  color?: string
  blank?: boolean
}

// The legal pages have a single canonical home on the marketing root
// domain; link there even from a workspace subdomain (e.g. the login and
// register screens live on app.<root>).
export const burgerMenuItems: IMenuItems[] = [
  {
    title: <Translation>{(t) => t("menu.confidentiality")}</Translation>,
    link: `${marketingOrigin()}/terms/privacy`,
  },
  {
    title: <Translation>{(t) => t("menu.terms")}</Translation>,
    link: `${marketingOrigin()}/terms/cgu`,
  },
  {
    title: <Translation>{(t) => t("menu.legal")}</Translation>,
    link: `${marketingOrigin()}/terms/legal`,
  },
  {
    title: <Translation>{(t) => t("menu.tutorial")}</Translation>,
    link: "/tutorial",
  },
  {
    title: <Translation>{(t) => t("menu.contact")}</Translation>,
    link: `mailto:${process.env.NEXT_PUBLIC_EMAIL}`,
    blank: true,
  },
]

const burgerMenuAccountItems: IMenuItems[] = [
  {
    title: <Translation>{(t) => t("menu.logout")}</Translation>,
    onClick: async () => {
      // Don't hand the redirect to next-auth: it resolves callbackUrl against
      // NEXTAUTH_URL, which isn't the workspace subdomain the user is on. Sign
      // out, notify the native shell, then navigate on the current host.
      await signOut({ redirect: false })
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({ isLoggedOut: true })
      )
      window.location.assign("/")
    },
    icon: ExternalLinkIcon,
    color: "red",
  },
]

const BurgerMenu = () => {
  const { status } = useSession()
  const isLoggedIn = status === "authenticated"
  const { isAdminHere } = useWorkspaceContext()
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <>
      <IconButton
        icon={<HamburgerIcon />}
        aria-label="menu"
        onClick={onOpen}
        variant="link"
        color="primaryColorText"
        size="lg"
      />
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent p={4} paddingTop={12}>
          <DrawerCloseButton />
          <VStack height="100%" justifyContent="space-between">
            <VStack spacing={10} alignItems="flex-start" width="100%">
              {burgerMenuItems.map((item, idx) => {
                return (
                  <NavigationButton
                    key={idx}
                    {...item}
                    type="text"
                    onClick={() => {
                      onClose()
                      item?.onClick && item.onClick()
                    }}
                  />
                )
              })}
              {isAdminHere && (
                <NavigationButton
                  title={<Translation>{(t) => t("workspace.admin.nav_link")}</Translation>}
                  link="/workspace/admin"
                  type="text"
                  onClick={onClose}
                />
              )}
            </VStack>
            {isLoggedIn && (
              <VStack spacing={6} alignItems="flex-start" width="100%">
                {/* On mobile the sidebar is hidden, so this is the only way
                    to reach another workspace. */}
                <WorkspaceSwitcher px={0} onNavigate={onClose} />
                {burgerMenuAccountItems.map((item, idx) => {
                  return (
                    <NavigationButton
                      key={idx}
                      {...item}
                      type="text"
                      onClick={() => {
                        onClose()
                        item?.onClick && item.onClick()
                      }}
                    />
                  )
                })}
              </VStack>
            )}
          </VStack>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export default BurgerMenu
